import { useEffect, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { LoaderCircle } from "lucide-react";
import { serverWakingAtom } from "../stores/serverWaking";
import { localeAtom } from "../stores/locale";
import { useT } from "../lib/i18n";

// Full-viewport overlay shown when the backend went unreachable (a read hit
// 502/503/504 and set serverWakingAtom — e.g. stg scaled to 0 during the idle
// window). It exists so a whole-server outage no longer collapses every read
// into empty/welcome states.
//
// It never shows blindly: it first probes GET /health. If /health is up, the 5xx
// was a single transient endpoint blip — it stands down without a flash. Only
// when /health is actually down does it render the "reconnecting" screen and
// poll; on the first healthy response it reloads so the sidebar + page reads all
// re-run cleanly against the now-awake backend (matches the wake Lambda's own
// meta-refresh recovery, and avoids trying to re-hydrate every atom by hand).
export function ServerWakingOverlay() {
  const [waking, setWaking] = useAtom(serverWakingAtom);
  const [confirmedDown, setConfirmedDown] = useState(false);
  const locale = useAtomValue(localeAtom);
  const t = useT(locale);

  useEffect(() => {
    if (!waking) {
      setConfirmedDown(false);
      return;
    }
    let cancelled = false;
    let wasDown = false;
    let timer: number | undefined;

    const probe = async () => {
      const healthy = await fetch("/health", { cache: "no-store" })
        .then((r) => r.ok)
        .catch(() => false);
      if (cancelled) return;
      if (healthy) {
        // Recovered after a confirmed outage → reload so every read re-runs.
        // Healthy on the FIRST probe → the 5xx was transient; stand down quietly.
        if (wasDown) window.location.reload();
        else setWaking(false);
        return;
      }
      if (!wasDown) {
        wasDown = true;
        setConfirmedDown(true);
      }
      timer = window.setTimeout(probe, 6000);
    };
    void probe();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [waking, setWaking]);

  if (!waking || !confirmedDown) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-2xl"
      style={{ background: "var(--bg-void)" }}
    >
      <div
        className="flex max-w-[28rem] flex-col items-center gap-lg px-3xl py-4xl text-center"
        style={{
          border: "1px solid var(--border)",
          borderRadius: "12px",
          background: "var(--bg-card)",
        }}
      >
        <span style={{ color: "var(--gold)", opacity: 0.9 }}>
          <LoaderCircle size={28} strokeWidth={1.5} className="animate-spin" />
        </span>
        <p
          style={{
            fontFamily: "var(--display)",
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "var(--text-strong)",
          }}
        >
          {t("serverWaking.title")}
        </p>
        <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>
          {t("serverWaking.body")}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md px-lg py-sm text-sm font-medium"
          style={{ border: "1px solid var(--gold)", color: "var(--gold)", background: "transparent" }}
        >
          {t("serverWaking.retry")}
        </button>
      </div>
    </div>
  );
}
